
import Vue from "vue";
import { CSSProperties } from "vue/types/jsx";
import { Component, Watch } from "vue-property-decorator";
import BuilderElement from "./BuilderElement.vue";
import NodeChrome from "./OverlaidUI/NodeChrome.vue";
import CanvasChrome from "./OverlaidUI/CanvasChrome.vue";
import {
  uiStore,
  documentStore,
  globalStore,
  configStore
} from "@/store/store";
import EmptyContainer from "@/components/Canvas/Empty/EmptyContainer.vue";
import SingularityEmptyContainer from "@/components/Canvas/Empty/SingularityEmptyContainer.vue";
import {
  handleSelectorsChanged,
  handleGlobalSettingsChanged,
  handlePresetsChanged,
  handleVariablesChanged
} from "@/lib/canvas/reacting-to-global-store-changes";
import { getBuilderCanvasDocument } from "@/lib/canvas/helpers";
import { handleOxySelectorsChanged } from "@/oxygen/classes/lib/builder";
import { updateSelectorsFromStorageEvent } from "@/oxygen/classes/lib/classes";
import InlineEditorBackdrop from "./InlineEditor/InlineEditorBackdrop.vue";
import DragGhostElement from "@/components/Add/DragGhostElement.vue";
import { isFutureLayerMode } from "@/futurelayer/ui/ui";
import {
  isTreeEmptyExceptForHeaderAndFooter,
  FUTURELAYER_HEADER_ELEMENT_TYPE,
} from "@/futurelayer/headers-footers/headers-footers";
import { TreeNode } from "@/types/tree";

@Component({
  components: {
    DragGhostElement,
    EmptyContainer,
    BuilderElement,
    NodeChrome,
    CanvasChrome
  }
})
export default class Canvas extends Vue {
  render() {
    const children = () => {
      if (this.canvasIsEmpty && !this.isFutureLayerMode) {
        return <EmptyContainer />;
      }

      if (!this.isFutureLayerMode) {
        return this.rootElement.children.map(element => (
          <BuilderElement
            // @ts-ignore
            key={element.id}
            // @ts-ignore
            parentId={this.rootElement.id}
            element={element}
          />
        ));
      } else {
        return this.elementsForCanvasInFutureLayerMode.map(item => {
          if (item.type === "empty-container") {
            return <SingularityEmptyContainer key="empty-container" />;
          }

          return (
            <BuilderElement
              // @ts-ignore
              key={item.element.id}
              // @ts-ignore
              parentId={this.rootElement.id}
              element={item.element}
            />
          );
        });
      }
    };

    const addAnotherSection = () => {
      if (this.isFutureLayerMode) {
        return <SingularityEmptyContainer />;
      } else {
        return undefined;
      }
    };

    return (
      <div
        key={this.canvasKey}
        class={this.classList}
        id="breakdance_canvas"
        style={this.style}
      >
        {children()}
        {/* {addAnotherSection()} */}
        {!this.isPreviewMode && <NodeChrome />}
        {(!this.isPreviewMode && !this.isOnboardingMode) ?? <CanvasChrome />}
        {!this.isPreviewMode && <CanvasChrome />}
        {!this.isPreviewMode && <InlineEditorBackdrop />}
        {!this.isPreviewMode && <DragGhostElement />}
        {this.previewSettings.backgroundColor && this.previewGlobalStyle()}
      </div>
    );
  }

  mounted() {
    uiStore.setCanvasState("normal");

    addEventListener("storage", this.onStorage);
  }

  beforeDestroy() {
    removeEventListener("storage", this.onStorage);
  }

  onStorage(event: StorageEvent) {
    if (event.key === "oxy_selectors") {
      updateSelectorsFromStorageEvent(event);
    }
  }

  get isFutureLayerMode() {
    return isFutureLayerMode();
  }

  get canvasKey() {
    return uiStore.canvasKey;
  }

  get builderMode() {
    return configStore.builderMode || "breakdance";
  }

  get isPreviewMode() {
    return uiStore.isPreviewMode;
  }

  get isOnboardingMode() {
    return window.Breakdance.isOnboarding;
  }

  get classList() {
    return {
      [this.builderMode]: true,
      canvas: true,
      dragging:
        uiStore.isCanvasElementBeingDragged || uiStore.isAddElementBeingDragged
    };
  }

  get rootElement() {
    return documentStore.document.tree.root;
  }

  get canvasIsEmpty() {
    if (this.isFutureLayerMode) {
      return isTreeEmptyExceptForHeaderAndFooter(documentStore.document.tree);
    }
    return this.rootElement.children.length === 0;
  }

  get elementsForCanvasInFutureLayerMode() {
    const elements : {
      type: "element" | "empty-container"
      element?: TreeNode
    }[] = this.rootElement.children.map(element => ({
      type: "element",
      element
    }));

    if (this.isFutureLayerMode && this.canvasIsEmpty) {
      const headerIndex = elements.findIndex(
        item => item.element?.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE
      );
      const insertIndex = headerIndex >= 0 ? headerIndex + 1 : 0;
      elements.splice(insertIndex, 0, { type: "empty-container" });
    }

    return elements;
  }

  get globalProperties() {
    return globalStore.globalProperties;
  }

  get selectors() {
    return globalStore.cssSelectors;
  }

  get presets() {
    return globalStore.presets;
  }

  get oxySelectors() {
    return globalStore.oxySelectors;
  }

  get variables() {
    return globalStore.variables;
  }

  get previewSettings() {
    const { preview } = documentStore.document.documentMeta.blockSettings || {
      preview: {}
    };

    return {
      width: preview.width,
      height: preview.height,
      backgroundColor: preview.backgroundColor,
      isCentered: preview.isCentered
    };
  }

  get style(): CSSProperties {
    const { height, width, isCentered } = this.previewSettings;

    const centerStyles: CSSProperties = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    };

    return {
      maxWidth: width ? `100%` : undefined,
      width: width ? `${width}px` : undefined,
      height: height ? `${height}px` : undefined,
      margin: width || height ? `0 auto` : undefined,
      ...(isCentered ? centerStyles : {})
    };
  }

  previewGlobalStyle() {
    const { backgroundColor } = this.previewSettings;
    const bgProperty =
      this.builderMode === "oxygen"
        ? "background-color"
        : "--bde-background-color";

    return (
      <style>
        {`
          :root,
         body {
          ${bgProperty}: ${backgroundColor};
         }
        `}
      </style>
    );
  }

  @Watch("globalProperties", { deep: true, immediate: true })
  onGlobalSettingsChanged() {
    handleGlobalSettingsChanged(getBuilderCanvasDocument());
  }

  @Watch("selectors", { deep: true, immediate: true })
  onSelectorsChanged() {
    handleSelectorsChanged(getBuilderCanvasDocument());
  }

  @Watch("presets", { deep: true, immediate: true })
  onPresetsChanged() {
    handlePresetsChanged(getBuilderCanvasDocument());
  }

  @Watch("oxySelectors", { deep: true, immediate: true })
  onOxySelectorsChanged() {
    handleOxySelectorsChanged(getBuilderCanvasDocument());
  }

  @Watch("variables", { deep: true, immediate: true })
  onVariablesChanged() {
    handleVariablesChanged(getBuilderCanvasDocument());
    handleOxySelectorsChanged(getBuilderCanvasDocument());
  }
}
